import { useState, useEffect } from 'react';
import {
  ensureValidUri,
  getPPUrl,
  getPPAuthToken,
} from '@r-and-a-shared-ui/utils';

import {
  CHAMPIONSHIP_PLAYERS_QUERY_KEY,
  CHAMPIONSHIP_CADDIES_QUERY_KEY,
  AUTH_TOKEN_KEY,
} from '../constants/playerPortals';

export type QueryResponse = {
  championshipPlayers?: Array<any>;
  championshipCaddies?: Array<any>;
  errorKey: string;
  httpStatusCode: number;
  successCode: string;
};

const useFetchOnSiteRegistrationData = (
  onSiteRegistrationAPI: any,
  championshipId?: string | undefined,
  dataSource?: string | undefined,
) => {
  const [playersData, setPlayersData] = useState<QueryResponse | null>(null);
  const [caddiesData, setCaddiesData] = useState<QueryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const isPlayersDataSource = dataSource?.toLowerCase() === 'players';

  const playersMutation =
    onSiteRegistrationAPI?.[CHAMPIONSHIP_PLAYERS_QUERY_KEY]?.useMutation();
  const caddiesMutation =
    onSiteRegistrationAPI?.[CHAMPIONSHIP_CADDIES_QUERY_KEY]?.useMutation();
  const authTokenMutation =
    onSiteRegistrationAPI?.[AUTH_TOKEN_KEY]?.useMutation();

  const playersCallbackUrl =
    process.env.NEXT_PUBLIC_GET_PLAYERS_BY_CHAMPIONSHIP_URL_PATH;
  const caddiesCallbackUrl =
    process.env.NEXT_PUBLIC_GET_CADDIES_BY_CHAMPIONSHIP_URL_PATH;

  const fetchData = async () => {
    setLoading(true);

    let bearerToken: string;

    try {
      bearerToken = await getPPAuthToken(authTokenMutation.mutateAsync);
    } catch (err: any) {
      console.error('Error fetching on site registration auth token:', err);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    if (isPlayersDataSource) {
      try {
        const playersRes = await playersMutation.mutateAsync({
          callBackUrl: ensureValidUri(playersCallbackUrl, getPPUrl()),
          bearerToken,
          championshipId,
        });
        setPlayersData(playersRes);
      } catch (err: any) {
        console.error('Error fetching championship players:', err);
      }

      setCaddiesData(null);
    } else {
      try {
        const caddiesRes = await caddiesMutation.mutateAsync({
          callBackUrl: ensureValidUri(caddiesCallbackUrl, getPPUrl()),
          bearerToken,
          championshipId,
        });
        setCaddiesData(caddiesRes);
      } catch (err: any) {
        console.error('Error fetching championship caddies:', err);
      }

      try {
        const playersRes = await playersMutation.mutateAsync({
          callBackUrl: ensureValidUri(playersCallbackUrl, getPPUrl()),
          bearerToken,
          championshipId,
        });
        setPlayersData(playersRes);
      } catch (err: any) {
        console.error('Error fetching championship players:', err);
      }
    }

    setLoading(false);
    setHasFetched(true);
  };

  useEffect(() => {
    const requiredCallbackUrl = isPlayersDataSource
      ? playersCallbackUrl
      : caddiesCallbackUrl;

    if (!championshipId || !requiredCallbackUrl) {
      setPlayersData(null);
      setCaddiesData(null);
      return;
    }

    fetchData();
  }, [championshipId, dataSource]);

  return { playersData, caddiesData, loading, hasFetched, refetch: fetchData };
};

export default useFetchOnSiteRegistrationData;
